import React from "react";

// Non-null only inside the /cashier-preview iframe surface. When set, its value
// is the CASHIER_PREVIEW_CONFIG object posted by the back-office builder; the
// cashier data hooks read it to short-circuit every live fetch/mutation so the
// builder preview reuses the REAL render components without ever hitting the API.
export const CashierPreviewContext = React.createContext(null);

// Default VIP shape mirrors useVipDataSource's `vip` (see VipView.js): tierName,
// progressPct, benefits[], nextTierName, toNextLabel, nextTierBenefits[]. Used
// when the posted config omits `vip` (or posts null) so the VIP tab renders a
// plausible tier instead of the live "not available" empty state.
export const DEFAULT_PREVIEW_VIP = {
  tierName: "Bronze",
  progressPct: 35,
  toNextLabel: "650 points",
  nextTierName: "Silver",
  benefits: [{ label: "Weekly cashback", value: "5%" }],
  nextTierBenefits: [{ label: "Weekly cashback", value: "8%" }],
};

// Default eligible-campaigns list (POST /deposit/campaigns response shape) used
// when the posted config omits `bonuses` or posts an empty list. Non-empty so
// the Bonuses tab and the deposit bonus-selector demo real content.
export const DEFAULT_PREVIEW_BONUSES = [
  {
    minimum_deposit: 20,
    bonus_campaign: { id: 9001, title: "100% Welcome Bonus" },
    schedule: { bonus_type: "Percentage", bonus: 100, maximum_bonus: 500 },
  },
  {
    minimum_deposit: 50,
    bonus_campaign: { id: 9002, title: "Weekend Reload 50%" },
    schedule: { bonus_type: "Percentage", bonus: 50, maximum_bonus: 250 },
  },
];

// Default active bonus (GET /bonus/active shape) so the Bonuses tab's top card
// demos a wagering-in-progress state.
export const DEFAULT_PREVIEW_ACTIVE_BONUS = {
  has_active_bonus: true,
  can_cancel: false,
  bonus_balance: 45,
  wagering_requirement: 400,
  wagering_achieved: 130,
};

// Default withdraw payload mirroring POST /withdraw/methods: methods[] rows
// (method_id/method/min_withdrawal in base currency) + details totals, plus the
// pending-withdrawals figure. Includes a COINSPAID row (exercises the crypto
// address+tag form) and a fields[] row (exercises the extra-fields form).
export const DEFAULT_PREVIEW_WITHDRAW = {
  methods: [
    { method_id: "INTERAC:ETO", method: "Interac e-Transfer", min_withdrawal: 20, logo: null },
    { method_id: "BANK:TRANSFER", method: "Bank Transfer", min_withdrawal: 50, logo: null },
    { method_id: "COINSPAID:BTC", method: "Bitcoin", min_withdrawal: 10, logo: null },
    {
      method_id: "WEBPAYMENT:PIX",
      method: "PIX",
      min_withdrawal: 10,
      logo: null,
      fields: [{ name: "document_id", label: "Document ID", type: "text" }],
    },
  ],
  details: { total_withdrawal_amount: 250 },
  pending: { amount: 0, currency: null },
};

// Default history rows (POST /history/transactions row shape; amounts arrive
// pre-formatted with the symbol). Dates are demo-static and span the filters.
export const DEFAULT_PREVIEW_HISTORY = [
  { type: "Deposit", method: "Interac e-Transfer", amount: "$50.00", status: "Approved", status_code: 1, action_date: "16-07-2026 14:12:09" },
  { type: "Withdrawal", method: "Bank Transfer", amount: "$120.00", status: "Pending", status_code: 0, action_date: "15-07-2026 09:41:33" },
  { type: "Deposit", method: "Visa", amount: "$25.00", status: "Approved", status_code: 1, action_date: "14-07-2026 21:05:57" },
  { type: "Withdrawal", method: "Bitcoin", amount: "$80.00", status: "Approved", status_code: 1, action_date: "12-07-2026 17:26:40" },
  { type: "Deposit", method: "Mastercard", amount: "$10.00", status: "Cancelled", status_code: 2, action_date: "10-07-2026 11:52:18" },
];

// Default active deposit limit (GET /deposit-limit row shape) so the Limits tab
// demos the locked-active state instead of only the empty form.
export const DEFAULT_PREVIEW_LIMIT = {
  id: 1,
  limit_amount: 500,
  start_date: "2026-07-01",
  end_date: "2026-08-01",
};
